/**
 * MediaLightbox — fullscreen media viewer primitive (approved mock 07).
 *
 * Image/video items from any surface open full-size over a dark stage with
 * zoom (images), batch ‹ › / ←→ navigation, «N of M» caption, a bottom action
 * bar and an optional theme-following meta side panel (renderMeta slot).
 *
 * Invariants (ui-components dialog rules):
 * - close button on the LEFT, exactly ONE dismiss affordance (Esc + X);
 * - the STAGE is intentionally dark in BOTH themes (media-viewing exception,
 *   documented in the Storybook story); all chrome around it uses theme tokens;
 * - single overlay level — never stack another dialog on top of it.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MEDIA_LIGHTBOX } from './selectors';
import {
  toggleZoom,
  zoomIn,
  zoomLabel,
  zoomOut,
  type MediaLightboxZoom,
} from './mediaLightboxZoom';

export interface MediaLightboxItem {
  id: string;
  kind: 'image' | 'video';
  src: string;
  poster?: string;
  title?: string;
  subtitle?: string;
  /** Defaults to src. */
  downloadUrl?: string;
  /** Defaults to src. */
  originalUrl?: string;
}

export interface MediaLightboxProps {
  open: boolean;
  onClose: () => void;
  items: MediaLightboxItem[];
  index: number;
  onIndexChange: (index: number) => void;
  /** Right-hand meta panel content (paper surface, theme-following). */
  renderMeta?: (item: MediaLightboxItem) => React.ReactNode;
  /** Renders an «Open details» action when provided. */
  onOpenDetails?: (item: MediaLightboxItem) => void;
}

/** Fixed dark stage palette — identical in light and dark themes. */
const STAGE_BG = 'rgba(10, 14, 17, 0.92)';
const STAGE_CHROME_BG = 'rgba(255, 255, 255, 0.12)';
const STAGE_TEXT = '#fff';
const STAGE_TEXT_DIM = 'rgba(255, 255, 255, 0.6)';

export function MediaLightbox({
  open,
  onClose,
  items,
  index,
  onIndexChange,
  renderMeta,
  onOpenDetails,
}: MediaLightboxProps): React.ReactElement | null {
  const [zoom, setZoom] = useState<MediaLightboxZoom>('fit');
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  const item = items[index] ?? null;
  const count = items.length;

  // Zoom and pan reset whenever the viewed item changes or the viewer reopens.
  useEffect(() => {
    setZoom('fit');
    setPan({ x: 0, y: 0 });
  }, [index, open, item?.id]);

  const goPrev = useCallback(() => {
    if (index > 0) onIndexChange(index - 1);
  }, [index, onIndexChange]);
  const goNext = useCallback(() => {
    if (index < count - 1) onIndexChange(index + 1);
  }, [count, index, onIndexChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext();
    }
  }, [goNext, goPrev]);

  const applyZoom = useCallback((next: MediaLightboxZoom) => {
    setZoom(next);
    setPan({ x: 0, y: 0 });
  }, []);

  const onStagePointerDown = useCallback((event: React.PointerEvent) => {
    if (zoom === 'fit') return;
    dragRef.current = { startX: event.clientX, startY: event.clientY, baseX: pan.x, baseY: pan.y };
  }, [pan.x, pan.y, zoom]);
  const onStagePointerMove = useCallback((event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    setPan({ x: drag.baseX + (event.clientX - drag.startX), y: drag.baseY + (event.clientY - drag.startY) });
  }, []);
  const onStagePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  if (!item) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      data-testid={MEDIA_LIGHTBOX.root}
      onKeyDown={handleKeyDown}
      PaperProps={{ sx: { bgcolor: STAGE_BG, color: STAGE_TEXT } }}
    >
      {/* Top bar: close on the LEFT (dialog invariant), title centre-left, counter right */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.25, py: 1.5 }}>
        <IconButton
          aria-label="Close media viewer"
          data-testid={MEDIA_LIGHTBOX.close}
          onClick={onClose}
          sx={{ bgcolor: STAGE_CHROME_BG, color: STAGE_TEXT, borderRadius: 2 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" data-testid={MEDIA_LIGHTBOX.title} sx={{ fontWeight: 500 }} noWrap>
            {item.title ?? item.id}
          </Typography>
          {item.subtitle && (
            <Typography variant="caption" sx={{ color: STAGE_TEXT_DIM }} noWrap>
              {item.subtitle}
            </Typography>
          )}
        </Box>
        <Typography
          variant="caption"
          data-testid={MEDIA_LIGHTBOX.counter}
          sx={{ ml: 'auto', color: STAGE_TEXT_DIM }}
        >
          {`${index + 1} of ${count}`}
        </Typography>
      </Stack>

      {/* Main: stage + optional meta panel */}
      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        <Box
          data-testid={MEDIA_LIGHTBOX.stage}
          onPointerDown={onStagePointerDown}
          onPointerMove={onStagePointerMove}
          onPointerUp={onStagePointerUp}
          onPointerLeave={onStagePointerUp}
          sx={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            cursor: zoom === 'fit' ? 'default' : 'grab',
          }}
        >
          <IconButton
            aria-label="Previous item"
            data-testid={MEDIA_LIGHTBOX.prev}
            onClick={goPrev}
            disabled={index === 0}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: STAGE_CHROME_BG,
              color: STAGE_TEXT,
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
              zIndex: 1,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {item.kind === 'video' ? (
            <Box
              key={item.id}
              component="video"
              controls
              autoPlay
              loop
              muted
              playsInline
              src={item.src}
              poster={item.poster}
              data-testid={MEDIA_LIGHTBOX.video}
              sx={{ maxWidth: '92%', maxHeight: '92%', borderRadius: 1, boxShadow: 8 }}
            />
          ) : (
            <Box
              key={item.id}
              component="img"
              src={item.src}
              alt={item.title ?? item.id}
              draggable={false}
              data-testid={MEDIA_LIGHTBOX.image}
              data-zoom={zoomLabel(zoom)}
              onDoubleClick={() => applyZoom(toggleZoom(zoom))}
              sx={{
                borderRadius: 1,
                boxShadow: 8,
                userSelect: 'none',
                ...(zoom === 'fit'
                  ? { maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }
                  : {
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom / 100})`,
                    transformOrigin: 'center center',
                  }),
              }}
            />
          )}

          <IconButton
            aria-label="Next item"
            data-testid={MEDIA_LIGHTBOX.next}
            onClick={goNext}
            disabled={index >= count - 1}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: STAGE_CHROME_BG,
              color: STAGE_TEXT,
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
              zIndex: 1,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {renderMeta && (
          <Paper
            data-testid={MEDIA_LIGHTBOX.metaPanel}
            sx={{ width: 320, flex: 'none', mr: 2.25, mb: 2.25, p: 2, overflowY: 'auto', borderRadius: 2 }}
          >
            {renderMeta(item)}
          </Paper>
        )}
      </Stack>

      {/* Bottom action bar */}
      <Stack direction="row" alignItems="center" spacing={1.75} sx={{ px: 2.25, py: 1.5 }}>
        {item.kind === 'image' && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ bgcolor: STAGE_CHROME_BG, borderRadius: 2, overflow: 'hidden' }}
          >
            <IconButton
              size="small"
              aria-label="Zoom out"
              data-testid={MEDIA_LIGHTBOX.zoomOut}
              onClick={() => applyZoom(zoomOut(zoom))}
              sx={{ color: STAGE_TEXT, borderRadius: 0 }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body2"
              data-testid={MEDIA_LIGHTBOX.zoomLabel}
              sx={{ px: 1, minWidth: 44, textAlign: 'center' }}
            >
              {zoomLabel(zoom)}
            </Typography>
            <IconButton
              size="small"
              aria-label="Zoom in"
              data-testid={MEDIA_LIGHTBOX.zoomIn}
              onClick={() => applyZoom(zoomIn(zoom))}
              sx={{ color: STAGE_TEXT, borderRadius: 0 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <Tooltip title="Fit to screen">
              <IconButton
                size="small"
                aria-label="Fit to screen"
                data-testid={MEDIA_LIGHTBOX.zoomFit}
                onClick={() => applyZoom('fit')}
                sx={{ color: STAGE_TEXT, borderRadius: 0 }}
              >
                <FitScreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
          Esc close · ← → navigate{item.kind === 'image' ? ' · double-click zoom' : ''}
        </Typography>
        <Stack direction="row" spacing={1.25} sx={{ ml: 'auto' }}>
          <IconButton
            size="small"
            aria-label="Download media"
            data-testid={MEDIA_LIGHTBOX.download}
            component="a"
            href={item.downloadUrl ?? item.src}
            download
            sx={{ color: STAGE_TEXT, border: '1px solid rgba(255,255,255,0.4)', borderRadius: 2 }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Open original in a new tab"
            data-testid={MEDIA_LIGHTBOX.openOriginal}
            component="a"
            href={item.originalUrl ?? item.src}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: STAGE_TEXT, border: '1px solid rgba(255,255,255,0.4)', borderRadius: 2 }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
          {onOpenDetails && (
            <IconButton
              size="small"
              aria-label="Open details"
              data-testid={MEDIA_LIGHTBOX.openDetails}
              onClick={() => onOpenDetails(item)}
              sx={{ color: STAGE_TEXT, border: '1px solid rgba(255,255,255,0.4)', borderRadius: 2 }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
